/**
 * PrintDialog — pick a saved printer, set copies, review warnings,
 * and send the label to the printer via the render-backend /api/v1/print.
 *
 * ZPL is regenerated inside the dialog using the SELECTED PRINTER'S linked
 * profile (printer.profileId), not the preview pane's current selection.
 * This is the key profile-truth invariant.
 */
import type { Template, Warning } from '@tsagko/label-contracts'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getPrinterProfiles } from '@tsagko/label-printer-profiles'
import { useAdapter } from '../adapters/AdapterProvider'
import { useToast } from '../ui/Toaster'
import { getSampleData } from '../preview/sample-data'
import { listPrinters, type SavedPrinter } from './printer-store'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PrintDialogProps {
  template: Template
  open: boolean
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PrintDialog({ template, open, onClose }: PrintDialogProps) {
  const adapter = useAdapter()
  const toast = useToast()
  const profiles = getPrinterProfiles()

  // Re-read printers each time the dialog opens so we see the latest list.
  const [printers, setPrinters] = useState<SavedPrinter[]>([])
  const [selectedPrinterId, setSelectedPrinterId] = useState('')
  const [copies, setCopies] = useState(1)
  const [zpl, setZpl] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<Warning[]>([])
  const [genError, setGenError] = useState<string | null>(null)
  const [printAnyway, setPrintAnyway] = useState(false)
  const [inFlight, setInFlight] = useState(false)

  const selectRef = useRef<HTMLSelectElement>(null)

  // When the dialog opens: refresh printer list, reset state.
  useEffect(() => {
    if (!open) return
    const list = listPrinters()
    setPrinters(list)
    setSelectedPrinterId(list[0]?.id ?? '')
    setCopies(1)
    setZpl(null)
    setWarnings([])
    setGenError(null)
    setPrintAnyway(false)
    setInFlight(false)
  }, [open])

  // Focus the printer select when dialog opens.
  useEffect(() => {
    if (open) {
      // Use a small timeout so the overlay has rendered before we focus.
      const tid = setTimeout(() => selectRef.current?.focus(), 10)
      return () => clearTimeout(tid)
    }
  }, [open])

  // Escape closes the dialog.
  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  // Regenerate ZPL whenever the selected printer changes or the template changes.
  const selectedPrinter = printers.find(p => p.id === selectedPrinterId) ?? null

  useEffect(() => {
    if (!open || !selectedPrinter || !adapter) {
      setZpl(null)
      setWarnings([])
      setGenError(null)
      return
    }

    // Invalidate stale output synchronously: a printer/copies change must
    // disable Print until the regenerated ZPL lands (a click in the stale
    // window would otherwise post the OLD ^PQ zpl), and "Print anyway"
    // consent must not transfer across warning sets.
    setZpl(null)
    setPrintAnyway(false)

    let cancelled = false
    const data = getSampleData('sample')
    void (async () => {
      const result = await adapter.operations.generateZpl({
        template,
        data,
        printerProfileId: selectedPrinter.profileId,
        opts: { copies },
      })
      if (cancelled) return
      if (result.status === 'ok') {
        setZpl(result.data.zpl)
        setWarnings(result.data.warnings)
        setGenError(null)
      } else {
        setZpl(null)
        setWarnings([])
        setGenError(result.error.message)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, adapter, template, selectedPrinter, copies])

  // ---------------------------------------------------------------------------
  // Print handler
  // ---------------------------------------------------------------------------

  async function handlePrint() {
    if (inFlight || !selectedPrinter || !zpl) return

    const backendUrl = import.meta.env.VITE_RENDER_BACKEND_URL
    const apiKey = import.meta.env.VITE_RENDER_API_KEY

    if (!backendUrl || !apiKey) {
      // surface inline — the user can't do anything about this at print time
      return
    }

    setInFlight(true)
    const idempotencyKey = crypto.randomUUID()
    try {
      const res = await fetch(`${backendUrl}/api/v1/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          zpl,
          printer: { host: selectedPrinter.host, port: selectedPrinter.port },
          copies,
          idempotencyKey,
        }),
      })
      if (res.ok) {
        toast.success(
          `Sent ${copies} label${copies === 1 ? '' : 's'} to ${selectedPrinter.name} (${selectedPrinter.host})`
        )
        onClose()
      } else {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null
        const msg = body?.error?.message ?? `HTTP ${res.status}`
        toast.error(
          `Failed to print to ${selectedPrinter.name} (${selectedPrinter.host}): ${msg}. ` +
          `Use Test connection on /printers to check reachability.`
        )
      }
    } catch (e) {
      toast.error(
        `Failed to print to ${selectedPrinter.name} (${selectedPrinter.host}): ` +
        `${e instanceof Error ? e.message : 'Network error'}. ` +
        `Use Test connection on /printers to check reachability.`
      )
    } finally {
      setInFlight(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasWarnings = warnings.length > 0
  const backendUrl = import.meta.env.VITE_RENDER_BACKEND_URL
  const apiKey = import.meta.env.VITE_RENDER_API_KEY
  const backendConfigured = Boolean(backendUrl && apiKey)

  const selectedProfile = selectedPrinter
    ? profiles.find(p => p.id === selectedPrinter.profileId)
    : null

  // DPI mismatch: the printer reported (via ~HI on Test connection) a
  // resolution different from the linked profile's — ZPL generated at the
  // profile DPI would print at the wrong physical size on this device.
  const dpiMismatch =
    selectedPrinter?.detectedDpi != null &&
    selectedProfile != null &&
    selectedPrinter.detectedDpi !== selectedProfile.dpi

  // Anything that requires explicit "Print anyway" consent.
  const gated = hasWarnings || dpiMismatch

  const printEnabled =
    !inFlight &&
    Boolean(selectedPrinter) &&
    Boolean(zpl) &&
    !genError &&
    backendConfigured &&
    (!gated || printAnyway)

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!open) return null

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="print-overlay"
        className="fixed inset-0 z-50 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        data-testid="print-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-dialog-title"
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-stone-200 bg-white p-6 shadow-lg dark:border-stone-700 dark:bg-stone-900"
      >
        <h2
          id="print-dialog-title"
          className="text-sm font-semibold text-stone-900 dark:text-stone-50"
        >
          Print label
        </h2>

        {printers.length === 0 ? (
          // Empty state
          <div
            data-testid="print-empty-state"
            className="mt-4 rounded border border-dashed border-stone-300 p-6 text-center dark:border-stone-700"
          >
            <p className="text-sm text-stone-600 dark:text-stone-400">
              No printers yet —{' '}
              <Link
                to="/printers"
                onClick={onClose}
                className="text-stone-900 underline dark:text-stone-100"
              >
                add one
              </Link>
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Printer select */}
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
                Printer
              </label>
              <select
                ref={selectRef}
                data-testid="print-printer-select"
                value={selectedPrinterId}
                onChange={e => setSelectedPrinterId(e.target.value)}
                className="w-full rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-950"
              >
                {printers.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.host}:{p.port})
                  </option>
                ))}
              </select>
            </div>

            {/* Profile info */}
            {selectedProfile && (
              <p className="text-xs text-stone-500 dark:text-stone-400">
                Profile: <span className="font-medium">{selectedProfile.name}</span>{' '}
                · {selectedProfile.dpi} dpi
              </p>
            )}

            {/* Copies */}
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
                Copies
              </label>
              <input
                data-testid="print-copies"
                type="number"
                min={1}
                max={999}
                value={copies}
                onChange={e => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v)) {
                    setCopies(Math.max(1, Math.min(999, v)))
                  }
                }}
                className="w-24 rounded border border-stone-300 bg-white px-3 py-1.5 text-sm outline-none focus:border-stone-500 dark:border-stone-700 dark:bg-stone-950"
              />
            </div>

            {/* ZPL gen error */}
            {genError && (
              <div className="rounded bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                ZPL generation failed: {genError}
              </div>
            )}

            {/* Env not configured */}
            {!backendConfigured && (
              <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                Print backend not configured: VITE_RENDER_BACKEND_URL and VITE_RENDER_API_KEY must be set.
              </div>
            )}

            {/* DPI mismatch warning */}
            {dpiMismatch && selectedPrinter && selectedProfile && (
              <div
                data-testid="print-dpi-mismatch"
                className="rounded bg-amber-50 px-3 py-1.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-300"
              >
                Printer reports {selectedPrinter.detectedDpi} dpi but the linked
                profile is {selectedProfile.dpi} dpi — printed size would be
                wrong. Fix the profile on the{' '}
                <Link to="/printers" onClick={onClose} className="underline">
                  Printers page
                </Link>
                .
              </div>
            )}

            {/* Warnings */}
            {hasWarnings && (
              <div data-testid="print-warnings" className="space-y-1">
                {warnings.map((w, i) => (
                  <div
                    key={i}
                    className="rounded bg-amber-50 px-3 py-1.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                  >
                    [{w.code}] {w.message}
                  </div>
                ))}
              </div>
            )}

            {/* Print anyway */}
            {gated && (
              <label className="flex items-center gap-2 text-xs text-stone-700 dark:text-stone-300">
                <input
                  data-testid="print-anyway"
                  type="checkbox"
                  checked={printAnyway}
                  onChange={e => setPrintAnyway(e.target.checked)}
                  className="rounded"
                />
                Print anyway
              </label>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-200 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          {printers.length > 0 && (
            <button
              type="button"
              data-testid="print-submit"
              disabled={!printEnabled}
              onClick={() => void handlePrint()}
              className="rounded bg-stone-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-stone-700 disabled:opacity-50 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
            >
              {inFlight ? 'Printing…' : 'Print'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}
